/* eslint-disable */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getAllChorusConfigAsync } from '../../api/chorus/chorusConfigApi';
import { getAllInvoicesAsync } from '../../api/chorus/chorusInvoiceApi';
import { getInvoicesStatusAsync } from '../../api/chorus/chorusInvoiceSubmissionApi';
import ChorusInvoice from '../../models/Chorus/ChorusInvoice';
import useFlashMessage from '../../stores/flashMessage';
import navigationService from '../../services/navigationService';

export interface Filters {
  itemPerPage: number;
  search?: string;
  order?: {
    property: string;
    type: 'ASC' | 'DESC';
  };
  date?: {
    before?: string;
    after?: string;
  };
  validity?: boolean;
}

export interface Configuration {
  id: string;
  account: string;
  supplierId: string;
}

export interface InvoiceState {
  status: string;
  statusComment?: string;
}

const useChorusStore = defineStore('chorus', () => {
  // State
  const page = ref<number>(1);
  const items = ref<Array<ChorusInvoice>>([]);
  const selected = ref<Array<ChorusInvoice>>([]);
  const totalItems = ref<number | undefined>(undefined);
  const filters = ref<Filters>({
    itemPerPage: 40,
    order: {
      property: 'date',
      type: 'DESC',
    },
  });
  const configuration = ref<Configuration | undefined>(undefined);

  // Getters
  const isInitialLoading = computed(() => totalItems.value === undefined);
  const account = computed(() => configuration.value?.account);

  // Helper function
  const getAllInvoices = async () => {
    const orderDate = filters.value.order?.property === 'date'
      ? filters.value.order.type
      : undefined;

    const orderReference = filters.value.order?.property === 'reference'
      ? filters.value.order.type
      : undefined;

    return getAllInvoicesAsync(
      page.value,
      filters.value.itemPerPage,
      filters.value.search,
      orderDate,
      orderReference,
      filters.value.validity,
      filters.value.date?.before,
      filters.value.date?.after,
    );
  };

  // Actions
  const setInvoices = (payload: Array<ChorusInvoice>) => {
    items.value = payload;
  };

  const setMoreInvoices = (payload: Array<ChorusInvoice>) => {
    items.value.push(...payload);
  };

  const setTotalItems = (total?: number) => {
    totalItems.value = total;
  };

  const setFilters = (newFilters: Filters) => {
    filters.value = newFilters;
  };

  const setPage = (newPage: number) => {
    page.value = newPage;
  };

  const setSelected = (newSelected: Array<ChorusInvoice>) => {
    selected.value = newSelected;
  };

  const toggleInvoice = (invoice: ChorusInvoice) => {
    const i = selected.value.findIndex((el) => el.id === invoice.id);

    if (i === -1) {
      selected.value.push(invoice);
    } else {
      selected.value.splice(i, 1);
    }
  };

  const setConfiguration = (config?: Configuration) => {
    configuration.value = config;
  };

  const loadInvoices = async () => {
    setTotalItems(undefined);
    setPage(1);

    const result = await getAllInvoices();

    setInvoices(result['hydra:member']);
    setTotalItems(result['hydra:totalItems']);
    await loadStatus();
  };

  const loadStatus = async () => {
    const numbers = items.value.map((invoice) => invoice.reference);

    try {
      const statuses = await getInvoicesStatusAsync(numbers);

      numbers.forEach((number) => {
        const i = items.value.findIndex(
          (invoice) => invoice.reference === number,
        );
        if (i !== -1) {
          items.value[i].state = null;
        }
      });

      statuses.forEach((status) => {
        const i = items.value.findIndex(
          (invoice) => invoice.reference === status.invoiceNumber,
        );
        if (i !== -1) {
          items.value[i].state = { status: status.status, statusComment: status.statusComment };
        }
      });
    } catch (error) {
      // En cas d'erreur, marquer toutes les factures avec un état d'erreur
      numbers.forEach((number) => {
        const i = items.value.findIndex(
          (invoice) => invoice.reference === number,
        );
        if (i !== -1) {
          items.value[i].state = { status: 'ERROR', statusComment: undefined };
        }
      });

      const flash = useFlashMessage();
      if (error instanceof Error && error.message === 'chorus-pro-api-unauthorized') {
        flash.error('chorus_pro_expired_message', 'chorus_pro_reconfigure', () => {
          navigationService.push({ name: 'ConfigurationPartnerApp' });
          flash.clear();
        }, false);
      } else if (error instanceof Error && error.message === 'chorus-pro-account-not-found') {
        flash.error('chorus_pro_not_configured_message', 'chorus_pro_configure', () => {
          navigationService.push({ name: 'ConfigurationPartnerApp' });
          flash.clear();
        }, false);
      } else {
        flash.error('chorus_pro_invoices_status_error');
      }
    }
  };

  const updateFilters = async (newFilters: Partial<Filters>) => {
    const mergedFilters = { ...filters.value, ...newFilters };
    setFilters(mergedFilters);
    return loadInvoices();
  };

  const loadMore = async () => {
    if (items.value.length === totalItems.value) return;

    setPage(page.value + 1);

    const result = await getAllInvoices();

    setMoreInvoices(result['hydra:member']);
    await loadStatus();
  };

  const loadConfiguration = async () => {
    const result = await getAllChorusConfigAsync(1);

    if (result['hydra:member'].length) {
      setConfiguration({
        id: result['hydra:member'][0].id,
        account: result['hydra:member'][0].account,
        supplierId: result['hydra:member'][0].supplierId,
      } as Configuration);
      return true;
    }
    return false;
  };

  return {
    // State
    page,
    items,
    selected,
    totalItems,
    filters,
    configuration,

    // Getters
    isInitialLoading,
    account,

    // Actions
    setInvoices,
    setMoreInvoices,
    setTotalItems,
    setFilters,
    setPage,
    setSelected,
    toggleInvoice,
    setConfiguration,
    loadInvoices,
    loadStatus,
    updateFilters,
    loadMore,
    loadConfiguration,
  };
});

export default useChorusStore;
